package domain.pieces

import engine.model.*
import engine.util.Logger
import kotlin.math.floor

open class GravinomePiece(
    override val id: Int,
    override val shape: Matrix,
    override val name: String,
    protected val kicks: TRSKicks? = null
) : Piece {

    override val isSpinEligible: Boolean = true

    override fun getRotationCenter(): Pair<Int, Int> = Pair((shape.rows - 1) / 2, (shape.cols - 1) / 2)

    override fun getRotationsState(rotationState: Int): Matrix {
        val turns = floor((rotationState % 4).toDouble()).toInt()
        val current = shape.copy()
        repeat(turns) {
            current.transpose()
            current.reverseRows()
        }
        return current
    }

    override fun getKickTable(rotation: Rotation, rotationState: Int): List<Pair<Int, Int>> {
        val table = kicks ?: return listOf(0 to 0)
        return when (rotation) {
            Rotation.ROTATE_CW -> table.cw[rotationState]
            Rotation.ROTATE_CCW -> table.ccw[rotationState]
            Rotation.ROTATE_180 -> table._180.getOrElse(rotationState) { listOf(0 to 0) }
        }
    }

    override fun getSpinType(
        board: Board,
        row: Int,
        col: Int,
        rotationState: Int,
        kickIndex: Int,
    ): SpinType {
        val shape = getRotationsState(rotationState)
        Logger.info { "kick index: $kickIndex" }

        if (canShift(board, shape, row + 1, col)) return SpinType.NONE

        if (matchesTShape()) return evaluateTSpin(board, row, col, rotationState, kickIndex)

        if (kickIndex == 0) return SpinType.NONE

        return evaluateImmobilitySpin(board, shape, row, col)
    }

    private fun evaluateTSpin(
        board: Board,
        row: Int,
        col: Int,
        rotationState: Int,
        kickIndex: Int,
    ): SpinType {
        val (centerRelRow, centerRelCol) = getRotationCenter()
        val centerRow = row + centerRelRow
        val centerCol = col + centerRelCol

        val corners = listOf(
            centerRow - 1 to centerCol - 1, // 0: TL
            centerRow - 1 to centerCol + 1, // 1: TR
            centerRow + 1 to centerCol + 1, // 2: BR
            centerRow + 1 to centerCol - 1  // 3: BL
        )

        val occupied = corners.map { (r, c) -> board.isOccupied(r, c) }
        val occupiedCount = occupied.count { it }

        if (occupiedCount < 3) return SpinType.NONE

        val frontCorners = when (rotationState) {
            0 -> listOf(0, 1)
            1 -> listOf(1, 2)
            2 -> listOf(2, 3)
            3 -> listOf(0, 3)
            else -> return SpinType.NONE
        }

        val frontBlocked = frontCorners.all { occupied[it] }

        return when {
            occupiedCount == 4 || frontBlocked -> SpinType.FULL
            kickIndex > 0 -> SpinType.MINI
            else -> SpinType.NONE
        }
    }

    private fun evaluateImmobilitySpin(
        board: Board,
        shape: Matrix,
        row: Int,
        col: Int,
    ): SpinType {
        val directions = listOf(0 to -1, 0 to 1, -1 to 0, 1 to 0)

        val totalBlocked = directions.count { (dr, dc) ->
            !canShift(board, shape, row + dr, col + dc)
        }

        val boardBlocked = directions.count { (dr, dc) ->
            boardCellInWay(board, shape, row + dr, col + dc)
        }

        return when {
            totalBlocked >= 3 && boardBlocked >= 2 -> SpinType.FULL
            totalBlocked >= 3 && boardBlocked >= 1 -> SpinType.FULL
            else -> SpinType.NONE
        }
    }

    /**
     * Returns true if the piece CAN occupy position ([row],[col]) —
     * no board cells in the way and all cells within bounds.
     */
    private fun canShift(board: Board, shape: Matrix, row: Int, col: Int): Boolean {
        for (r in 0 until shape.rows) {
            for (c in 0 until shape.cols) {
                if (shape[r, c] == 0) continue
                val br = row + r
                val bc = col + c
                if (br !in 0 until board.rows || bc !in 0 until board.cols) return false
                if (board[br, bc] != 0) return false
            }
        }
        return true
    }

    /**
     * Returns true if at least one piece cell at ([row],[col]) would overlap
     * an existing **board cell** (ignores out-of-bounds — walls are not board cells).
     */
    private fun boardCellInWay(board: Board, shape: Matrix, row: Int, col: Int): Boolean {
        for (r in 0 until shape.rows) {
            for (c in 0 until shape.cols) {
                if (shape[r, c] == 0) continue
                val br = row + r
                val bc = col + c
                if (br !in 0 until board.rows || bc !in 0 until board.cols) continue
                if (board[br, bc] != 0) return true
            }
        }
        return false
    }

    /**
     * Returns true for pieces that use T-spin corner detection:
     * the T tetromino
     */
    private fun matchesTShape(): Boolean = (this.id == Gravinome.T.id).also { Logger.info { "matchesTShape" } }
}